/* eslint-disable react/no-did-update-set-state */
/**
 * Component for search inputs in FTUX, Nav and onboarding
 *
 * @module components/GlobalSearchInput
 */

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { injectIntl } from 'react-intl';

import CloseIcon from '../../../../../static/icons/close.svg';
import SearchIcon from '../../../../../static/icons/ic_search.svg';
import Icon from '../../../../components/Icon/Icon';
import { AriaRoles } from '../../../../constants/AccessibilityConstants';
import { KeyCodes } from '../../../../constants/Keyboard';
import QA from '../../../../constants/QATags';
import { messages as m } from '../../constants';
import * as D from '../../deps';

import './GlobalSearchInput.scss';

interface IGlobalSearchInputProps {
  focusPlaceholder?: $TSFixMe;
  initialFocus?: boolean;
  intl?: $TSFixMe;
  isCenterPageSearch: boolean;
  isOnboardingPage: boolean;
  isRightRailActive: boolean;
  isSearchOpen: boolean;
  navTheme: string;
  onInputBlur: $TSFixMeFunction;
  onInputChange: $TSFixMeFunction;
  onInputClear: $TSFixMeFunction;
  onInputFocus: $TSFixMeFunction;
  overrideFocus?: boolean;
  overrideSearchTerm?: string;
  placeholder?: $TSFixMe;
  shouldDropSearchToNextLine?: boolean;
  showColdStart?: boolean;
}

class GlobalSearchInput extends React.PureComponent<IGlobalSearchInputProps> {
  static defaultProps = {
    placeholder: m.placeholderDefault,
    focusPlaceholder: m.placeholderDefault,
  };

  constructor(props: IGlobalSearchInputProps) {
    super(props);

    this.textInput = React.createRef();
    this.lastKeyDownCode = -1;
  }

  state = {
    isFocused: false,
    initialized: false,
  };

  componentDidMount(): $TSFixMe {
    if (this.props.initialFocus) {
      this.textInput.current.focus();
    }
    if (!isEmpty(this.props.overrideSearchTerm)) {
      this.textInput.current.value = this.props.overrideSearchTerm;
      this.forceUpdate(); // Need to re-render so that hasSearchTerm accurately reflects input state
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'prevProps' implicitly has an 'any' type... Remove this comment to see the full error message
  componentDidUpdate(prevProps, prevState): $TSFixMeFunction {
    if (this.shouldFocusAfterUpdate(prevProps, prevState)) {
      this.textInput.current.focus();
    }
    if (this.shouldBlurAfterUpdate(prevProps, prevState)) {
      this.textInput.current.blur();
    }
    if (prevProps.isSearchOpen && !this.props.isSearchOpen) {
      this.textInput.current.value = '';
      this.resetFocus();
      this.forceUpdate(); // Need to re-render so that hasSearchTerm accurately reflects input state
    }
    // Update search term in input after navigating back to search from history
    if (this.shouldReplaceQueryAfterUpdate(prevProps)) {
      const validOverride = this.lastKeyDownCode !== KeyCodes.BACKSPACE;
      if (validOverride) {
        this.textInput.current.value = this.props.overrideSearchTerm;
        this.forceUpdate(); // Need to re-render so that hasSearchTerm accurately reflects input state
      }
    }
  }

  componentWillUnmount(): $TSFixMe {
    this.stopPoller();
  }

  textInput: $TSFixMe;
  lastKeyDownCode;
  inputPoller: $TSFixMe;

  shouldFocusAfterUpdate: $TSFixMeFunction = (prevProps, prevState) => {
    return (
      (prevProps.overrideFocus !== this.props.overrideFocus &&
        this.props.overrideFocus &&
        !this.state.isFocused) ||
      (prevState.isFocused !== this.state.isFocused && this.state.isFocused)
    );
  };

  shouldBlurAfterUpdate: $TSFixMeFunction = (_prevProps, prevState) => {
    return prevState.isFocused !== this.state.isFocused && !this.state.isFocused;
  };

  shouldReplaceQueryAfterUpdate: $TSFixMeFunction = _prevProps => {
    return (
      !isEmpty(this.props.overrideSearchTerm) &&
      this.textInput.current.value !== this.props.overrideSearchTerm
    );
  };

  shouldSubmitQueryAfterPolling: $TSFixMeFunction = val => {
    return val === this.lastInputValue && val !== this.lastSubmittedValue;
  };

  lastInputValue = '';
  lastSubmittedValue = '';

  pollInputValue: $TSFixMeFunction = () => {
    const val = get(this, 'textInput.current.value');

    if (val === this.lastSubmittedValue) return;

    D.Utils.Time.markTimeAtMomentForQuery(
      D.Constants.App.SearchTriggerMarks.SEARCH_STATS_INPUT_CHANGE,
      val,
    );

    if (this.shouldSubmitQueryAfterPolling(val)) {
      this.submitQueryValue(val);
      return;
    }

    // this handles the first keystroke that happens before search is open
    // the openSearch mediator handles the initial keystroke that opens search &
    // the subsequent query updates from the continuing user keystrokes
    if (!this.props.isSearchOpen) {
      this.submitQueryValue(val);
    }

    this.lastInputValue = val;
  };

  submitQueryValue: $TSFixMeFunction = newInputValue => {
    this.props.onInputChange(newInputValue);
    this.lastSubmittedValue = newInputValue;
  };

  hasSearchTerm: $TSFixMeFunction = () => {
    // We need to get the input value here because in the case of clearing the input with the clear link,
    // the searchTerm prop won't have updated yet and this function will incorrectly report there is a
    // non-empty search term
    return !!get(this, 'textInput.current.value');
  };

  // NOTE: we use polling because directly binding to the input field value and using onChange directly was
  // locking up the text input when typing quickly
  startPoller: $TSFixMeFunction = () => {
    this.inputPoller = setInterval(this.pollInputValue, D.Constants.App.SEARCH_POLLING_FREQ_MSEC);
  };

  stopPoller: $TSFixMeFunction = () => {
    clearInterval(this.inputPoller);
  };

  handleChange: $TSFixMeFunction = event => {
    // Only for the initial input to open search
    const val = event.target.value;
    if (val && val.length <= 1) {
      this.submitQueryValue(val);
    }
  };

  handleFocus: $TSFixMeFunction = () => {
    this.props.onInputFocus(this.hasSearchTerm());
    this.startFocus();
    this.startPoller();
  };

  handleBlur: $TSFixMeFunction = () => {
    this.props.onInputBlur();
    if (this.hasSearchTerm() || !this.props.isSearchOpen) {
      this.resetFocus();
    }
    this.stopPoller();
  };

  handleKeyDown: $TSFixMeFunction = e => {
    this.lastKeyDownCode = e.keyCode;
    if (e.keyCode === KeyCodes.ENTER) {
      const val = get(this, 'textInput.current.value');
      this.submitQueryValue(val);
    }
  };

  startFocus: $TSFixMeFunction = () => {
    this.setState({ isFocused: true });
  };

  resetFocus: $TSFixMeFunction = () => {
    this.setState({ isFocused: false });
  };

  isActiveInput: $TSFixMeFunction = keyCode => {
    return ![
      KeyCodes.TAB,
      KeyCodes.SHIFT,
      KeyCodes.ESC,
      KeyCodes.LEFT_COMMAND,
      KeyCodes.RIGHT_COMMAND,
      KeyCodes.FIREFOX_COMMAND,
      KeyCodes.CONTROL,
      KeyCodes.ALT,
    ].includes(keyCode);
  };

  handleClearButton: $TSFixMeFunction = event => {
    event.stopPropagation();
    event.preventDefault();
    this.props.onInputClear(event);
    this.textInput.current.focus();
    this.textInput.current.value = '';
  };

  render(): $TSFixMe {
    const t = this.props.intl.formatMessage;
    const hasSearchTerm = this.hasSearchTerm();
    const classes = D.Utils.Css.createBEM('GlobalSearchInput', {
      centerPageSearch: this.props.isCenterPageSearch && !this.props.isSearchOpen,
      coldStartWelcome: this.props.showColdStart && !this.props.isOnboardingPage,
      emptyFocused: this.state.isFocused && !hasSearchTerm,
      inputFilled: hasSearchTerm,
      nav:
        !this.props.showColdStart && !this.props.isOnboardingPage && !this.props.isCenterPageSearch,
      onboarding: this.props.isOnboardingPage,
      [this.props.navTheme]: !isEmpty(this.props.navTheme) && !this.props.isOnboardingPage,
      rightRail: this.props.isRightRailActive,
      searchOpen: this.props.isSearchOpen && !this.props.showColdStart,
      shouldDropSearchToNextLine: this.props.shouldDropSearchToNextLine,
    });

    return (
      <div className={classes}>
        <div className='GlobalSearchInput__inner' role='search'>
          <input
            aria-activedescendant=''
            aria-autocomplete='both'
            aria-controls='results'
            aria-describedby='searchKeyboardControls'
            aria-haspopup={hasSearchTerm}
            aria-label='Search Pandora'
            autoCapitalize='none'
            autoComplete='off'
            className='GlobalSearchInput__input'
            data-qa={QA.HSET}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            placeholder={t(
              this.state.isFocused ? this.props.focusPlaceholder : this.props.placeholder,
            )}
            ref={this.textInput}
            spellCheck='false'
            type='search'
          />
          <Icon className='GlobalSearchInput__iconSearch' glyph={SearchIcon} qaTag={QA.HSI} />
          <button
            className='GlobalSearchInput__clear'
            data-qa={QA.SFC}
            onClick={this.handleClearButton}
            type='button'
          >
            <span className='visuallyHidden'>Clear search</span>
            <Icon
              className='GlobalSearchInput__clear__icon'
              glyph={CloseIcon}
              role={AriaRoles.PRESENTATION}
            />
          </button>
        </div>
      </div>
    );
  }
}

export default injectIntl(GlobalSearchInput);
